import type { IFinancialPlanQuality } from 'partner360/features/financialPlanQuality'
import type { ISobMandatoryContext } from 'partner360/features/financialPlanQuality'
import type { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type { IFinancialCockpitActualsAndTargetsSetting } from '../../api/types'
import type { CockpitPeriodKey, ICockpitCategoryOption, IPartnerFinSobRow } from '../../model/types'

import { averageScore } from './averageScore'
import { getFinancialPlanQualityFromCockpitRows } from './getFinancialPlanQualityFromCockpitRows'

export interface ICockpitFinancialPlanQualitySummary {
  byOffering: ReadonlyMap<string, IFinancialPlanQuality>
  overallQuality: IFinancialPlanQuality
  averageOfferingScore: number | null
}

interface IParams {
  rows: IPartnerFinSobRow[]
  fYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  periods: CockpitPeriodKey[]
  currentFQuarter: number
  fQuarterActualsPercentage: number
  offeringPresentation: Array<{ offering: string; offeringName: string }>
  categoriesByOffering?: Partial<Record<string, ICockpitCategoryOption[]>>
  sobMandatoryContext?: ISobMandatoryContext
  settings?: IFinancialCockpitActualsAndTargetsSetting[]
}

export const getCockpitFinancialPlanQualitySummary = ({
  rows,
  fYear,
  actualsType,
  isLocalCurrency,
  periods,
  currentFQuarter,
  fQuarterActualsPercentage,
  offeringPresentation,
  categoriesByOffering,
  settings,
  sobMandatoryContext,
}: IParams): ICockpitFinancialPlanQualitySummary => {
  const getQuality = (offering?: string, category: string | null = null) =>
    getFinancialPlanQualityFromCockpitRows({
      rows,
      fYear,
      actualsType,
      isLocalCurrency,
      offering,
      category,
      periods,
      currentFQuarter,
      fQuarterActualsPercentage,
      offeringPresentation,
      categoriesByOffering,
      settings,
      sobMandatoryContext,
    })

  const byOffering = new Map(
    offeringPresentation.map(({ offering }) => [offering, getQuality(offering)] as const)
  )
  const overallQuality = getQuality()
  return {
    byOffering,
    overallQuality,
    averageOfferingScore: averageScore(
      Array.from(byOffering.values(), ({ overall }) => overall.score)
    ),
  }
}
